'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import SubscribeModalPortal from './SubscribeModalPortal'
import SearchModalPortal from './SearchModalPortal'
import HeaderSubscribeButton from './HeaderSubscribeButton'

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showNewsletterModal, setShowNewsletterModal] = useState(false)
  const [showSearchModal, setShowSearchModal] = useState(false)
  const pathname = usePathname()

  const toggleMobileMenu = () => {
    console.log('Toggle menu clicked, current state:', isMobileMenuOpen)
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const openNewsletterModal = () => {
    setShowNewsletterModal(true)
  }

  const closeNewsletterModal = () => {
    setShowNewsletterModal(false)
  }

  const openSearchModal = () => {
    setShowSearchModal(true)
  }

  const closeSearchModal = () => {
    setShowSearchModal(false)
  }


  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD/CTRL + K or just / when not in an input
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearchModal(true)
      } else if (e.key === '/' && e.target === document.body) {
        e.preventDefault()
        setShowSearchModal(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <div className="w-full flex justify-center sticky top-0 z-50">
        <div className="backdrop-blur-[20px] backdrop-filter bg-white border-b border-black relative w-full max-w-[1440px]">
            <header
              className="relative w-full h-14 md:h-16 lg:h-20"
              role="banner"
            >
          <div className="flex flex-row items-center relative w-full h-full">
            <div className="box-border flex flex-row items-center justify-between px-6 lg:px-12 py-0 relative w-full h-full">
          {/* Logo - Show on tablets and desktop */}
          <div className="hidden md:flex flex-col justify-center relative shrink-0 z-10">
            <Link 
              href="/" 
              className="text-[24px] md:text-[28px] lg:text-fluid-2xl text-black leading-tight tracking-premium block transition-opacity duration-200 hover:opacity-60"
              aria-label="Vibe & Make - Home"
              style={{ fontFamily: 'var(--font-gilda-display), serif' }}
            >
              Vibe & Make
            </Link>
          </div>

          {/* Navigation - Show earlier on tablets */}
          <nav 
            className="hidden md:flex flex-row font-ibm-plex-sans-condensed gap-6 md:gap-8 lg:gap-10 h-16 md:h-20 items-center justify-center text-black text-[12px] md:text-[13px] lg:text-[18px] text-center tracking-wider uppercase absolute left-0 right-0 mx-auto"
            role="navigation"
            aria-label="Main navigation"
          >
            <Link 
              href="/tools" 
              prefetch={true}
              className="flex flex-col h-full justify-center relative shrink-0 focus:outline-none border-0 outline-none"
              style={{ outline: 'none', border: 'none' }}
              aria-label="Browse AI creative tools"
            >
              <span className="block leading-[14px] transition-opacity duration-200 hover:opacity-60 outline-none" style={{ outline: 'none' }}>TOOLS</span>
              {pathname === '/tools' && (
                <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-red-500 z-10" />
              )}
            </Link>
            <Link 
              href="/makers" 
              prefetch={true}
              className="flex flex-col h-full justify-center relative shrink-0 focus:outline-none border-0 outline-none"
              style={{ outline: 'none', border: 'none' }}
              aria-label="Discover AI creators and makers"
            >
              <span className="block leading-[14px] transition-opacity duration-200 hover:opacity-60 outline-none" style={{ outline: 'none' }}>MAKERS</span>
              {pathname === '/makers' && (
                <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-red-500 z-10" />
              )}
            </Link>
            {/* TEMPORARILY DISABLED - PROJECTS SECTION
                TODO: Re-enable when projects content is ready
                Uncomment the Link below to restore projects navigation */}
            {/* <Link 
              href="/projects" 
              prefetch={true}
              className="flex flex-col h-full justify-center relative shrink-0 focus:outline-none border-0 outline-none"
              style={{ outline: 'none', border: 'none' }}
              aria-label="View community AI projects"
            >
              <span className="block leading-[14px] transition-opacity duration-200 hover:opacity-60 outline-none" style={{ outline: 'none' }}>PROJECTS</span>
              {pathname === '/projects' && (
                <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-red-500 z-10" />
              )}
            </Link> */}
            <Link 
              href="/news" 
              prefetch={true}
              className="flex flex-col h-full justify-center relative shrink-0 focus:outline-none border-0 outline-none"
              style={{ outline: 'none', border: 'none' }}
              aria-label="Read AI creative news"
            >
              <span className="block leading-[14px] transition-opacity duration-200 hover:opacity-60 outline-none" style={{ outline: 'none' }}>NEWS</span>
              {pathname === '/news' && (
                <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-red-500 z-10" />
              )}
            </Link>
          </nav>

          {/* Desktop Actions - Show on tablets too */}
          <div className="hidden md:flex flex-row gap-4 md:gap-6 items-center justify-end relative shrink-0 z-10">
            {/* Search Box */}
            <button
              onClick={openSearchModal}
              className="flex items-center gap-3 px-4 py-2 border border-black transition-all duration-200 hover:bg-black hover:text-white focus:outline-none focus:bg-black focus:text-white group min-w-[200px] lg:min-w-[240px]"
              aria-label="Search (Press / or ⌘K)"
              type="button"
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="text-black group-hover:text-white flex-shrink-0"
              >
                <path 
                  d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <span className="font-ibm-plex-sans-condensed text-black group-hover:text-white text-[12px] md:text-[13px] tracking-wider uppercase flex-1 text-left">Search</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 font-ibm-plex-sans-condensed text-[10px] text-black group-hover:text-white border border-black group-hover:border-white">
                ⌘K
              </kbd>
            </button>

            {/* Subscribe Button */}
            <HeaderSubscribeButton openNewsletterModal={openNewsletterModal} />
          </div>

          {/* Mobile Header Layout - Only on mobile, not tablets */}
          <div className="flex md:hidden w-full items-center justify-between">
            {/* Mobile Menu Button - Fixed container to prevent shifting */}
            <div className="w-[57px] h-4 flex items-center justify-start flex-shrink-0 overflow-visible">
              <button
                className="w-full h-full flex items-center justify-start"
                aria-label={isMobileMenuOpen ? "Close menu" : "Toggle menu"}
                onClick={toggleMobileMenu}
                type="button"
              >
                {isMobileMenuOpen ? (
                  <Image 
                    src="/mobile-menu-close.svg" 
                    alt="Close menu"
                    width={14}
                    height={14}
                    className="w-3.5 h-3.5"
                  />
                ) : (
                  <Image 
                    src="/mobile-menu-hamburger.svg" 
                    alt="Open menu"
                    width={57}
                    height={12}
                    className="h-3 w-[57px]"
                  />
                )}
              </button>
            </div>
            
            {/* Mobile Logo - Adjusted spacing to account for full hamburger width */}
            <Link 
              href="/" 
              className="text-[20px] text-black leading-[1.2] font-medium block -ml-[29px] mt-0.5"
              aria-label="Vibe & Make - Home"
              style={{ fontFamily: 'var(--font-gilda-display), serif' }}
            >
              Vibe & Make
            </Link>
            
            {/* Spacer to push actions to the right */}
            <div className="flex-1" />
            
            {/* Mobile Actions - Search and Subscribe */}
            <div className="flex items-center gap-3">
              {/* Mobile Search Button */}
              <button
                onClick={openSearchModal}
                className="flex items-center justify-center w-6 h-6 transition-opacity duration-200 hover:opacity-60"
                aria-label="Search"
                type="button"
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-black"
                >
                  <path 
                    d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              
              {/* Mobile Subscribe Button */}
              <HeaderSubscribeButton openNewsletterModal={openNewsletterModal} />
            </div>
          </div>
            </div>
          </div>
        </header>
        </div>
      </div>
    
    {/* Mobile Menu - Overlay positioning */}
    {isMobileMenuOpen && (
      <div className="w-full flex justify-center fixed top-14 left-0 right-0 z-40">
        <div className="lg:hidden bg-white border-b border-black w-full max-w-[1440px]">
        <nav 
          className="flex flex-col font-ibm-plex-sans-condensed gap-10 px-6 py-10 text-black text-[14px] leading-[14px] tracking-[1.4px] uppercase"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <Link 
            href="/tools" 
            className="block w-fit"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            TOOLS
          </Link>
          <Link 
            href="/makers" 
            className="block w-fit"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            MAKERS
          </Link>
          {/* TEMPORARILY DISABLED - PROJECTS SECTION
              TODO: Re-enable when projects content is ready
              Uncomment the Link below to restore projects mobile navigation */}
          {/* <Link 
            href="/projects" 
            className="block w-fit"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            PROJECTS
          </Link> */}
          <Link 
            href="/news" 
            className="block w-fit"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            NEWS
          </Link>
        </nav>
        </div>
      </div>
    )}
      
      {/* Search Modal - Rendered outside header */}
      <SearchModalPortal 
        isOpen={showSearchModal} 
        onClose={closeSearchModal} 
      />
      
      {/* Subscribe Modal - Rendered outside header */}
      <SubscribeModalPortal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
      />
    </>
  )
}

export default Header